import React, { useState, useEffect } from "react";
import { useSuspense } from "@rest-hooks/react";
import PropTypes from "prop-types";

import TicketFilter from "./TicketFilter";
import Header from "./Header";
import Stats from "./Stats";
import NewTicketModal from "./NewTicketModal.js";

import DataSelectionTable from "components/DataTable/DataSelectionTable";
import MerchCollectionsList from "components/MerchCollectionsList";
import TabPanels from "components/TabPanels";

import Content from "./TicketDetailsModal/Content";
import Actions from "./TicketDetailsModal/Actions";

import useScanRedeem from "components/ScanRedeem/useScanRedeem";
import { ticketsColumns } from "utils/tables/columns/ticketsColumns";
import usePaginationAndFilteringController from "utils/usePaginationAndFilteringController";
import useEndSalesController from "utils/useEndSalesController";
import fetchScannable from "utils/fetchScannable";
import shouldShowMerchTab from "./shouldShowMerchTab";

const TicketsList = ({TicketResource, ticketResourceParams, MerchResource, merchResourceParams, event, sessionId=null}) => {

  const [openNewTicketModal, setOpenNewTicketModal] = useState(false);

  const {
    allow_checkout,
    single,
    sessions,
    tickets,
    event_products,
    has_merch_collections,
  } = event;

  // Merch collection lives as its own tab (alongside Tickets / Stats), shown
  // while products are listed or after listing removal when sold items still
  // belong to the event. It is the non-ticket counterpart of check-in, kept on
  // a separate tab so staff never confuse the two.
  const hasMerch = shouldShowMerchTab({
    hasMerchResource: Boolean(MerchResource),
    eventProducts: event_products,
    hasMerchCollections: has_merch_collections,
  });

  const stats = useSuspense(TicketResource.list(), {...ticketResourceParams, stats: true});

  const {imported_tickets_count} = stats[0];

  const session = !single && sessions.find(session => session.id === +sessionId);
  const selectableTickets = session ? session.tickets : tickets;
  const filteredTickets = selectableTickets.filter(ticket => ticket.sellable && ticket.available)

  // Sellable merch for the event's cash-sale modal — same sellable/available gate
  // as tickets, over the event's active product listings.
  const filteredMerchProducts = (event_products || []).filter(product => product.sellable && product.available);

  // End-of-sale targets: every active sellable listing, regardless of remaining
  // availability — ending a sold-out listing still releases its reserved stock.
  const endableMerchProducts = (event_products || []).filter(product => product.sellable);

  const {
    objectState,
    fetchData,
    handleFilterChange,
    handleQueryChange,
    handlePageChange
  } = usePaginationAndFilteringController({
      fetchDataResource: TicketResource,
      fetchDataParams: ticketResourceParams,
  });

  //fetchTickets on mount
  useEffect(() => {
    fetchData();
  }, [ticketResourceParams]);

  // Shared scan → redeem core (also used by merch collection). The ticket-only
  // shell below — selling, ending sales, stats — stays here.
  const { openScanner, openDetailsByUuid, renderScannerAndModal } = useScanRedeem({
    Resource: TicketResource,
    resourceParams: ticketResourceParams,
    fetchItem: (params) => fetchScannable({ ...params, resourcePath: "tickets" }),
    forwardIntent: "check_in",
    allowReverse: allow_checkout,
    scanTitle: allow_checkout ? "Scan QR Code" : "Check In",
    toastFor: (intent, amount) => {
      if (intent === "check_out") return "Check Out Successful";
      if (intent === "approve") return "Approved Successfully";
      return amount > 1 ? `Checked In ${amount} Tickets Successfully` : "Checked In Successfully";
    },
    buildModalContent: (ticket, handleAction) => ({
      title: <strong>{ticket.title}</strong>,
      content: <Content ticket={ticket} />,
      button: (
        <Actions
          uuid={ticket.uuid}
          redeemState={ticket.redeem_state}
          orderAmount={ticket.order_amount}
          orderItems={ticket.order_items}
          allowCheckout={allow_checkout}
          onClick={handleAction}
        />
      ),
      fullScreen: true,
    }),
    onDone: fetchData,
  });

  const handleOpenNewTicketModal = () => {
    setOpenNewTicketModal(true);
  };

  const {
    openEndSalesModal,
    EndSalesModalComponent,
    handleEndSalesModalOpen
  } = useEndSalesController(
    event,
    TicketResource,
    ticketResourceParams
  );

  return (
    <>
      <TabPanels labels={hasMerch ? ["Tickets", "Stats", "Merch"] : ["Tickets", "Stats"]}>
        <>
          <Header
            onCheckIn={openScanner}
            onSellTickets={handleOpenNewTicketModal}
            openEndSalesModal={handleEndSalesModalOpen}
            allowCheckout={allow_checkout}
          />

          <DataSelectionTable
            records={objectState.filteredObjects}

            columns={ticketsColumns(
              (row) => openDetailsByUuid(row.uuid), imported_tickets_count
            )}

            searchLabel="Search by Ticket ref, Order or Member"
            onQueryChange={handleQueryChange}
            queryString={objectState.query}

            serverPaginationProps={{
              paginationServer: true,
              paginationTotalRows: objectState.totalCount,
              page: objectState.page,
              onChangePage: handlePageChange
            }}

            filter={
              <TicketFilter
                onChange={handleFilterChange}
              />
            }
          />
        </>

        <div>
          <Stats
            TicketResource={TicketResource}
            ticketResourceParams={ticketResourceParams}
            stats={stats}
          />
        </div>

        {hasMerch && (
          <div>
            <MerchCollectionsList
              Resource={MerchResource}
              resourceParams={merchResourceParams}
              products={filteredMerchProducts}
              endableProducts={endableMerchProducts}
              event={event}
              TicketResource={TicketResource}
              ticketResourceParams={ticketResourceParams}
            />
          </div>
        )}
      </TabPanels>

      {renderScannerAndModal()}

      {openNewTicketModal && (
        <NewTicketModal
          tickets={filteredTickets}
          onClose={() => setOpenNewTicketModal(false)}
          open={openNewTicketModal}
          done={fetchData}
          TicketResource={TicketResource}
          ticketResourceParams={ticketResourceParams}
        />
      )}

      {openEndSalesModal &&
        <EndSalesModalComponent />}
    </>
  );
};

TicketsList.propTypes = {
  TicketResource: PropTypes.object,
  ticketResourceParams: PropTypes.object,
  MerchResource: PropTypes.object,
  merchResourceParams: PropTypes.object,
  event: PropTypes.object,
  sessionId: PropTypes.string,
};

export default TicketsList;
